import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  DollarSign, Plus, Edit, Trash2, Save, X, RefreshCw,
  Globe, Ship, Plane, Truck, Percent, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TariffConfigPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [activeTab, setActiveTab] = useState('routes');
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [surcharges, setSurcharges] = useState([]);
  const [countries, setCountries] = useState([]);
  const [services, setServices] = useState([]);
  const [containerTypes, setContainerTypes] = useState([]);
  
  // Dialog states
  const [isRouteDialogOpen, setIsRouteDialogOpen] = useState(false);
  const [isSurchargeDialogOpen, setIsSurchargeDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingSurcharge, setEditingSurcharge] = useState(null);
  
  // Form states
  const [routeForm, setRouteForm] = useState({
    originCountry: '',
    destinationCountry: '',
    serviceType: '',
    containerType: '',
    minRate: '',
    maxRate: '',
    transitTimeDaysMin: '',
    transitTimeDaysMax: '',
    currency: 'EUR'
  });
  
  const [surchargeForm, setSurchargeForm] = useState({
    name: '',
    code: '',
    type: 'flat',
    value: '',
    currency: 'EUR',
    appliesTo: []
  });

  useEffect(() => {
    if (isAuthenticated ) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = {  };
      
      const [routesRes, surchargesRes, countriesRes, servicesRes, containersRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/routes`, { headers }),
        fetch(`${API_URL}/api/admin/surcharges`, { headers }),
        fetch(`${API_URL}/api/catalog/countries`),
        fetch(`${API_URL}/api/catalog/services`),
        fetch(`${API_URL}/api/catalog/container-types`)
      ]);
      
      if (routesRes.ok) setRoutes(await routesRes.json());
      if (surchargesRes.ok) setSurcharges(await surchargesRes.json());
      if (countriesRes.ok) setCountries(await countriesRes.json());
      if (servicesRes.ok) setServices(await servicesRes.json());
      if (containersRes.ok) setContainerTypes(await containersRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('tariff.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRoute = async () => {
    try {
      const headers = { 
        'Content-Type': 'application/json',
         
      };
      
      const payload = {
        ...routeForm,
        minRate: parseFloat(routeForm.minRate),
        maxRate: parseFloat(routeForm.maxRate),
        transitTimeDaysMin: parseInt(routeForm.transitTimeDaysMin),
        transitTimeDaysMax: parseInt(routeForm.transitTimeDaysMax)
      };
      
      const url = editingRoute 
        ? `${API_URL}/api/admin/routes/${editingRoute._id}`
        : `${API_URL}/api/admin/routes`;
      const method = editingRoute ? 'PUT' : 'POST';
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      
      if (res.ok) {
        toast.success(t('tariff.rateSaved'));
        setIsRouteDialogOpen(false);
        setEditingRoute(null);
        resetRouteForm();
        fetchData();
      } else {
        toast.error(t('tariff.saveError'));
      }
    } catch (error) {
      toast.error(t('tariff.connectionError'));
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!confirm(t('tariff.deleteRate'))) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/routes/${routeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(t('tariff.rateDeleted'));
        fetchData();
      }
    } catch (error) {
      toast.error(t('tariff.deleteError'));
    }
  };

  const handleSaveSurcharge = async () => {
    try {
      const headers = { 
        'Content-Type': 'application/json',
         
      };
      
      const payload = {
        ...surchargeForm,
        value: parseFloat(surchargeForm.value)
      };
      
      const url = editingSurcharge 
        ? `${API_URL}/api/admin/surcharges/${editingSurcharge._id}`
        : `${API_URL}/api/admin/surcharges`;
      const method = editingSurcharge ? 'PUT' : 'POST';
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      
      if (res.ok) {
        toast.success(t('tariff.surchargeSaved'));
        setIsSurchargeDialogOpen(false);
        setEditingSurcharge(null);
        resetSurchargeForm();
        fetchData();
      } else {
        toast.error(t('tariff.saveError'));
      }
    } catch (error) {
      toast.error(t('tariff.connectionError'));
    }
  };

  const handleDeleteSurcharge = async (surchargeId) => {
    if (!confirm(t('tariff.deleteSurcharge'))) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/surcharges/${surchargeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(t('tariff.surchargeDeleted'));
        fetchData();
      }
    } catch (error) {
      toast.error(t('tariff.deleteError'));
    }
  };

  const resetRouteForm = () => {
    setRouteForm({
      originCountry: '',
      destinationCountry: '',
      serviceType: '',
      containerType: '',
      minRate: '',
      maxRate: '',
      transitTimeDaysMin: '',
      transitTimeDaysMax: '',
      currency: 'EUR'
    });
  };

  const resetSurchargeForm = () => {
    setSurchargeForm({
      name: '',
      code: '',
      type: 'flat',
      value: '',
      currency: 'EUR',
      appliesTo: []
    });
  };

  const openEditRoute = (route) => {
    setEditingRoute(route);
    setRouteForm({
      originCountry: route.originCountry || '',
      destinationCountry: route.destinationCountry || '',
      serviceType: route.serviceType || '',
      containerType: route.containerType || '',
      minRate: route.minRate?.toString() || '',
      maxRate: route.maxRate?.toString() || '',
      transitTimeDaysMin: route.transitTimeDaysMin?.toString() || '',
      transitTimeDaysMax: route.transitTimeDaysMax?.toString() || '',
      currency: route.currency || 'EUR'
    });
    setIsRouteDialogOpen(true);
  };

  const openEditSurcharge = (surcharge) => {
    setEditingSurcharge(surcharge);
    setSurchargeForm({
      name: surcharge.name || '',
      code: surcharge.code || '',
      type: surcharge.type || 'flat',
      value: surcharge.value?.toString() || '',
      currency: surcharge.currency || 'EUR',
      appliesTo: surcharge.appliesTo || []
    });
    setIsSurchargeDialogOpen(true);
  };

  // Auth checks
  if (authLoading) {
    return (
      <div className="min-h-screen bg-steel-50 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-steel-50 pt-24" data-testid="tariff-config-page">
      {/* Header */}
      <section className="bg-navy-900 py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
                {t('tariff.title')}
              </h1>
              <p className="text-steel-300">
                {t('tariff.subtitle')}
              </p>
            </div>
            <Button 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
              onClick={fetchData}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('tariff.refresh')}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-6 bg-white border-b border-steel-200">
        <div className="container-custom">
          <div className="flex gap-4">
            {[
              { id: 'routes', label: t('tariff.routeRates'), icon: Globe },
              { id: 'surcharges', label: t('tariff.surcharges'), icon: Percent }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-navy-800 text-white'
                    : 'bg-steel-100 text-steel-600 hover:bg-steel-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
              <p className="text-steel-600">{t('tariff.loading')}</p>
            </div>
          ) : (
            <>
              {/* Route Rates Tab */}
              {activeTab === 'routes' && (
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{t('tariff.routeRates')}</CardTitle>
                    <Button 
                      onClick={() => { resetRouteForm(); setEditingRoute(null); setIsRouteDialogOpen(true); }}
                      className="btn-accent"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('tariff.newRate')}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {routes.length === 0 ? (
                      <div className="text-center py-12">
                        <Globe className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                        <p className="text-steel-600">
                          {t('tariff.noRoutes')}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('tariff.origin')}</TableHead>
                              <TableHead>{t('tariff.destination')}</TableHead>
                              <TableHead>{t('tariff.serviceType')}</TableHead>
                              <TableHead>{t('tariff.containerType')}</TableHead>
                              <TableHead>{t('tariff.minRate')}</TableHead>
                              <TableHead>{t('tariff.maxRate')}</TableHead>
                              <TableHead>{t('tariff.transitTime')}</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {routes.map(route => (
                              <TableRow key={route._id}>
                                <TableCell className="font-medium">{route.originCountry}</TableCell>
                                <TableCell>{route.destinationCountry}</TableCell>
                                <TableCell>{route.serviceType}</TableCell>
                                <TableCell>{route.containerType || '-'}</TableCell>
                                <TableCell>{route.currency} {route.minRate}</TableCell>
                                <TableCell>{route.currency} {route.maxRate}</TableCell>
                                <TableCell>{route.transitTimeDaysMin}-{route.transitTimeDaysMax} {t('tariff.days')}</TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => openEditRoute(route)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteRoute(route._id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Surcharges Tab */}
              {activeTab === 'surcharges' && (
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{t('tariff.surcharges')}</CardTitle>
                    <Button 
                      onClick={() => { resetSurchargeForm(); setEditingSurcharge(null); setIsSurchargeDialogOpen(true); }}
                      className="btn-accent"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('tariff.newSurcharge')}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {surcharges.length === 0 ? (
                      <div className="text-center py-12">
                        <Percent className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                        <p className="text-steel-600">
                          {t('tariff.noSurcharges')}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('tariff.name')}</TableHead>
                              <TableHead>{t('tariff.code')}</TableHead>
                              <TableHead>{t('tariff.type')}</TableHead>
                              <TableHead>{t('tariff.value')}</TableHead>
                              <TableHead>{t('tariff.appliesTo')}</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {surcharges.map(surcharge => (
                              <TableRow key={surcharge._id}>
                                <TableCell className="font-medium">{surcharge.name}</TableCell>
                                <TableCell>
                                  <span className="bg-steel-100 px-2 py-1 rounded text-sm">{surcharge.code}</span>
                                </TableCell>
                                <TableCell>
                                  {surcharge.type === 'flat' ? (t('tariff.flat')) : '%'}
                                </TableCell>
                                <TableCell>
                                  {surcharge.type === 'flat' 
                                    ? `${surcharge.currency} ${surcharge.value}`
                                    : `${surcharge.value}%`
                                  }
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {(surcharge.appliesTo || []).slice(0, 2).map(s => (
                                      <span key={s} className="bg-navy-100 text-navy-800 text-xs px-2 py-1 rounded">
                                        {s}
                                      </span>
                                    ))}
                                    {(surcharge.appliesTo || []).length > 2 && (
                                      <span className="text-xs text-steel-500">+{surcharge.appliesTo.length - 2}</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => openEditSurcharge(surcharge)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteSurcharge(surcharge._id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </section>

      {/* Route Dialog */}
      <Dialog open={isRouteDialogOpen} onOpenChange={setIsRouteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingRoute 
                ? (t('tariff.editRate'))
                : (t('tariff.newRate'))
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.origin')}</label>
                <Select value={routeForm.originCountry} onValueChange={(v) => setRouteForm({...routeForm, originCountry: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('tariff.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.destination')}</label>
                <Select value={routeForm.destinationCountry} onValueChange={(v) => setRouteForm({...routeForm, destinationCountry: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('tariff.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.serviceType')}</label>
                <Select value={routeForm.serviceType} onValueChange={(v) => setRouteForm({...routeForm, serviceType: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('tariff.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s._id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.containerType')}</label>
                <Select value={routeForm.containerType} onValueChange={(v) => setRouteForm({...routeForm, containerType: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('tariff.selectContainer')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">-</SelectItem>
                    {containerTypes.map(c => (
                      <SelectItem key={c._id} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.minRate')}</label>
                <Input 
                  type="number" 
                  value={routeForm.minRate}
                  onChange={(e) => setRouteForm({...routeForm, minRate: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.maxRate')}</label>
                <Input 
                  type="number" 
                  value={routeForm.maxRate}
                  onChange={(e) => setRouteForm({...routeForm, maxRate: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.currency')}</label>
                <Select value={routeForm.currency} onValueChange={(v) => setRouteForm({...routeForm, currency: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.transitTime') + ' Min'}</label>
                <Input 
                  type="number" 
                  value={routeForm.transitTimeDaysMin}
                  onChange={(e) => setRouteForm({...routeForm, transitTimeDaysMin: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.transitTime') + ' Max'}</label>
                <Input 
                  type="number" 
                  value={routeForm.transitTimeDaysMax}
                  onChange={(e) => setRouteForm({...routeForm, transitTimeDaysMax: e.target.value})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRouteDialogOpen(false)}>
              {t('tariff.cancel')}
            </Button>
            <Button onClick={handleSaveRoute} className="btn-accent">
              <Save className="w-4 h-4 mr-2" />
              {t('tariff.saved')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Surcharge Dialog */}
      <Dialog open={isSurchargeDialogOpen} onOpenChange={setIsSurchargeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSurcharge 
                ? (t('tariff.editSurcharge'))
                : (t('tariff.newSurcharge'))
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.name')}</label>
                <Input 
                  value={surchargeForm.name}
                  onChange={(e) => setSurchargeForm({...surchargeForm, name: e.target.value})}
                  placeholder="Fuel Surcharge"
                />
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.code')}</label>
                <Input 
                  value={surchargeForm.code}
                  onChange={(e) => setSurchargeForm({...surchargeForm, code: e.target.value.toUpperCase()})}
                  placeholder="FUEL"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.type')}</label>
                <Select value={surchargeForm.type} onValueChange={(v) => setSurchargeForm({...surchargeForm, type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">{t('tariff.flat')}</SelectItem>
                    <SelectItem value="percentage">{t('tariff.percentage')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.value')}</label>
                <Input 
                  type="number" 
                  value={surchargeForm.value}
                  onChange={(e) => setSurchargeForm({...surchargeForm, value: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-steel-600 mb-1 block">{t('tariff.currency')}</label>
                <Select 
                  value={surchargeForm.currency} 
                  onValueChange={(v) => setSurchargeForm({...surchargeForm, currency: v})}
                  disabled={surchargeForm.type === 'percentage'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm text-steel-600 mb-2 block">{t('tariff.appliesTo')}</label>
              <div className="grid grid-cols-2 gap-2">
                {services.map(service => (
                  <label key={service._id} className="flex items-center gap-2 p-2 rounded border border-steel-200 cursor-pointer hover:bg-steel-50">
                    <input
                      type="checkbox"
                      checked={surchargeForm.appliesTo.includes(service.name)}
                      onChange={(e) => {
                        const newAppliesTo = e.target.checked
                          ? [...surchargeForm.appliesTo, service.name]
                          : surchargeForm.appliesTo.filter(s => s !== service.name);
                        setSurchargeForm({...surchargeForm, appliesTo: newAppliesTo});
                      }}
                      className="rounded border-steel-300"
                    />
                    <span className="text-sm">{service.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSurchargeDialogOpen(false)}>
              {t('tariff.cancel')}
            </Button>
            <Button onClick={handleSaveSurcharge} className="btn-accent">
              <Save className="w-4 h-4 mr-2" />
              {t('tariff.saved')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TariffConfigPage;
